(() => {
  const DRAG_THRESHOLD = 6;

  function initializeSection(section) {
    if (!section || section.dataset.whyUsSliderInitialized === "true") return;

    const track = section.querySelector("[data-slider-track]");
    const prevButton = section.querySelector("[data-slider-prev]");
    const nextButton = section.querySelector("[data-slider-next]");
    const indicators = Array.from(
      section.querySelectorAll("[data-slider-dot]"),
    );
    const autoplayEnabled = section.dataset.autoplay === "true";
    const autoplaySpeed = Number.parseInt(
      section.dataset.autoplaySpeed || "0",
      10,
    );
    const stopAutoplayOnInteraction =
      section.dataset.stopOnInteraction === "true";
    const loopSlides = section.dataset.loopSlides === "true";
    let autoplayTimer = null;
    let autoplayStoppedByInteraction = false;

    if (!track) return;
    section.dataset.whyUsSliderInitialized = "true";

    const desktopMQ = window.matchMedia("(min-width: 1024px)");
    let isDesktop = desktopMQ.matches;
    let desktopIndex = 0;

    const getSlides = () =>
      Array.from(track.querySelectorAll(".why-us-slider__slide"));

    const getStep = () => {
      const slides = getSlides();
      if (!slides.length) return 0;
      if (slides.length === 1) {
        const ts = window.getComputedStyle(track);
        const gap = parseFloat(ts.gap) || 0;
        return slides[0].offsetWidth + gap;
      }
      return slides[1].offsetLeft - slides[0].offsetLeft;
    };

    const computeVisibleCount = () => {
      const slides = getSlides();
      if (!slides.length) return 1;
      const ts = window.getComputedStyle(track);
      const paddingLeft = parseFloat(ts.paddingLeft) || 0;
      const paddingRight = parseFloat(ts.paddingRight) || 0;
      const gap = parseFloat(ts.gap) || 0;
      const innerWidth = track.clientWidth - paddingLeft - paddingRight;
      const step = getStep();
      if (step === 0) return 1;
      return Math.max(1, Math.floor((innerWidth + gap) / step + 0.05));
    };

    let zIndexTimers = [];
    let wrapResetTimer = null;
    let isAnimating = false;
    let animationLockTimer = null;

    const setSlideZIndex = (slide, newZ) => {
      const oldZ = parseInt(slide.style.zIndex || "2", 10);
      if (newZ > oldZ) {
        const timer = window.setTimeout(() => {
          slide.style.zIndex = String(newZ);
        }, 500);
        zIndexTimers.push(timer);
      } else {
        slide.style.zIndex = String(newZ);
      }
    };

    const applyDesktopTransforms = (instant = false) => {
      const slides = getSlides();
      const step = getStep();
      const visibleCount = computeVisibleCount();
      const lastVisible = desktopIndex + visibleCount - 1;

      zIndexTimers.forEach((t) => window.clearTimeout(t));
      zIndexTimers = [];
      if (wrapResetTimer) {
        window.clearTimeout(wrapResetTimer);
        wrapResetTimer = null;
      }

      if (instant) {
        slides.forEach((s) => {
          s.style.transition = "none";
        });
      }

      slides.forEach((slide, i) => {
        const isVisible = i >= desktopIndex && i <= lastVisible;
        const isStackedLeft = i === desktopIndex - 1;
        const isStackedRight = i === lastVisible + 1;
        const isStacked = isStackedLeft || isStackedRight;
        const isHiddenLeft = i < desktopIndex - 1;
        const isHidden = !isVisible && !isStacked;

        slide.classList.toggle("is-hidden", isHidden);
        slide.classList.toggle("is-stacked", isStacked);

        if (isVisible) {
          slide.style.transform = `translateX(${-desktopIndex * step}px) scale(1)`;
          slide.style.opacity = "";
          slide.style.pointerEvents = "";
          slide.removeAttribute("aria-hidden");
          if (instant) slide.style.zIndex = "2";
          else setSlideZIndex(slide, 2);
        } else if (isStackedLeft) {
          slide.style.transform = `translateX(${-i * step}px) scale(0.5)`;
          slide.style.opacity = "1";
          slide.style.pointerEvents = "none";
          slide.setAttribute("aria-hidden", "true");
          if (instant) slide.style.zIndex = "1";
          else setSlideZIndex(slide, 1);
        } else if (isStackedRight) {
          slide.style.transform = `translateX(${(visibleCount - 1 - i) * step}px) scale(0.5)`;
          slide.style.opacity = "1";
          slide.style.pointerEvents = "none";
          slide.setAttribute("aria-hidden", "true");
          if (instant) slide.style.zIndex = "1";
          else setSlideZIndex(slide, 1);
        } else if (isHiddenLeft) {
          slide.style.transform = `translateX(${-i * step}px) scale(0)`;
          slide.style.opacity = "0";
          slide.style.pointerEvents = "none";
          slide.setAttribute("aria-hidden", "true");
          if (instant) slide.style.zIndex = "0";
          else setSlideZIndex(slide, 0);
        } else {
          slide.style.transform = `translateX(${(visibleCount - 1 - i) * step}px) scale(0)`;
          slide.style.opacity = "0";
          slide.style.pointerEvents = "none";
          slide.setAttribute("aria-hidden", "true");
          if (instant) slide.style.zIndex = "0";
          else setSlideZIndex(slide, 0);
        }
      });

      if (instant) {
        void track.offsetWidth;
        slides.forEach((s) => {
          s.style.transition = "";
        });
      }
    };

    const clearDesktopStyles = () => {
      getSlides().forEach((slide) => {
        slide.style.transform = "";
        slide.style.opacity = "";
        slide.style.zIndex = "";
        slide.style.pointerEvents = "";
        slide.classList.remove("is-stacked", "is-hidden");
        slide.removeAttribute("aria-hidden");
      });
    };

    const getSnapScrollLeft = (index) => {
      const slides = getSlides();
      const slide = slides[index];
      if (!slide) return 0;
      return Math.max(
        0,
        slide.offsetLeft + slide.offsetWidth / 2 - track.clientWidth / 2,
      );
    };

    const findCenterActiveIndex = () => {
      const slides = getSlides();
      if (!slides.length) return 0;
      const scrollLeft = track.scrollLeft;
      let closest = 0;
      let bestDist = Infinity;
      slides.forEach((_, i) => {
        const dist = Math.abs(scrollLeft - getSnapScrollLeft(i));
        if (dist < bestDist) {
          bestDist = dist;
          closest = i;
        }
      });
      return closest;
    };

    const getSwipeTargetIndex = (commitThreshold) => {
      const slides = getSlides();
      const scrollDelta = track.scrollLeft - startScrollLeft;
      const direction = Math.sign(scrollDelta);

      if (!direction || Math.abs(scrollDelta) <= DRAG_THRESHOLD) {
        return dragStartIndex;
      }

      const candidate = dragStartIndex + direction;
      if (candidate < 0 || candidate >= slides.length) {
        return dragStartIndex;
      }

      const fromSnap = getSnapScrollLeft(dragStartIndex);
      const toSnap = getSnapScrollLeft(candidate);
      const travel = Math.abs(toSnap - fromSnap);
      if (!travel) return dragStartIndex;

      const progress = Math.abs(track.scrollLeft - fromSnap) / travel;
      return progress >= commitThreshold ? candidate : dragStartIndex;
    };

    const findActiveIndexEarly = (scrollDirection = 0) => {
      const slides = getSlides();
      if (!slides.length) return 0;

      if (isDragging && hasDragged) {
        return getSwipeTargetIndex(0.12);
      }

      const scrollLeft = track.scrollLeft;
      const direction =
        scrollDirection !== 0
          ? scrollDirection
          : Math.sign(track.scrollLeft - lastScrollLeft);

      if (!direction) {
        return findCenterActiveIndex();
      }

      let fromIndex = 0;
      if (direction > 0) {
        slides.forEach((_, i) => {
          if (getSnapScrollLeft(i) <= scrollLeft + 1) {
            fromIndex = i;
          }
        });
      } else {
        for (let i = 0; i < slides.length; i++) {
          if (getSnapScrollLeft(i) >= scrollLeft - 1) {
            fromIndex = i;
            break;
          }
        }
      }

      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= slides.length) {
        return fromIndex;
      }

      const fromSnap = getSnapScrollLeft(fromIndex);
      const toSnap = getSnapScrollLeft(toIndex);
      const travel = Math.abs(toSnap - fromSnap);
      if (!travel) return fromIndex;

      const progress = Math.abs(scrollLeft - fromSnap) / travel;
      const EARLY_SWITCH_RATIO = 0.33;

      return progress >= EARLY_SWITCH_RATIO ? toIndex : fromIndex;
    };

    const setMobileActiveIndex = (index) => {
      const slides = getSlides();
      if (!slides.length) return;
      const clampedIndex = Math.max(0, Math.min(slides.length - 1, index));
      updateActiveSlideClass(clampedIndex);
      updateDots(clampedIndex);

      const isAtStart = clampedIndex <= 0;
      const isAtEnd = clampedIndex >= slides.length - 1;
      if (prevButton) {
        prevButton.disabled = !loopSlides && isAtStart;
        prevButton.classList.toggle("is-inactive", isAtStart);
      }
      if (nextButton) {
        nextButton.disabled = !loopSlides && isAtEnd;
        nextButton.classList.toggle("is-inactive", isAtEnd);
      }
    };

    const updateActiveSlideClass = (index) => {
      getSlides().forEach((slide, i) => {
        slide.classList.toggle("is-active", i === index);
        slide.classList.toggle("is-before-active", i < index);
        slide.classList.toggle("is-after-active", i > index);
      });
    };

    const updateDots = (index) => {
      indicators.forEach((dot, dotIndex) => {
        dot.classList.toggle("is-active", dotIndex === index);
      });
    };

    const updateControls = () => {
      if (isDesktop) {
        const slides = getSlides();
        const visibleCount = computeVisibleCount();
        const maxIndex = Math.max(0, slides.length - visibleCount);
        const isAtStart = desktopIndex <= 0;
        const isAtEnd = desktopIndex >= maxIndex;
        if (prevButton) {
          prevButton.disabled = !loopSlides && isAtStart;
          prevButton.classList.toggle("is-inactive", isAtStart);
        }
        if (nextButton) {
          nextButton.disabled = !loopSlides && isAtEnd;
          nextButton.classList.toggle("is-inactive", isAtEnd);
        }
        updateDots(desktopIndex);
        return;
      }

      const slides = getSlides();
      if (!slides.length) return;
      setMobileActiveIndex(findCenterActiveIndex());
    };

    const scrollToCenterSlide = (index, behavior = "smooth") => {
      const slides = getSlides();
      if (!slides.length) return;
      const clampedIndex = Math.max(0, Math.min(slides.length - 1, index));
      const slide = slides[clampedIndex];
      const targetLeft =
        slide.offsetLeft + slide.offsetWidth / 2 - track.clientWidth / 2;

      if (!isDesktop) {
        setMobileActiveIndex(clampedIndex);
      }

      track.scrollTo({ left: Math.max(0, targetLeft), behavior });
    };

    const applyDesktopWrap = (oldDesktopIndex) => {
      const slides = getSlides();
      const step = getStep();

      zIndexTimers.forEach((t) => window.clearTimeout(t));
      zIndexTimers = [];
      if (wrapResetTimer) {
        window.clearTimeout(wrapResetTimer);
        wrapResetTimer = null;
      }

      slides.forEach((slide) => {
        slide.style.transition = "none";
        slide.style.transform = `translateX(${-oldDesktopIndex * step}px) scale(1)`;
        slide.style.opacity = "1";
        slide.style.zIndex = "2";
        slide.style.pointerEvents = "";
        slide.classList.remove("is-stacked", "is-hidden");
        slide.removeAttribute("aria-hidden");
      });

      void track.offsetWidth;

      slides.forEach((slide) => {
        slide.style.transition = "";
        slide.style.transform = `translateX(${-desktopIndex * step}px) scale(1)`;
      });

      wrapResetTimer = window.setTimeout(() => {
        applyDesktopTransforms(true);
        wrapResetTimer = null;
      }, 500);
    };

    const stepByDirection = (direction) => {
      if (isDesktop) {
        if (isAnimating) return;
        const slides = getSlides();
        const visibleCount = computeVisibleCount();
        const maxIndex = Math.max(0, slides.length - visibleCount);
        let newIndex = desktopIndex + direction;
        let isWrapping = false;
        if (loopSlides) {
          if (newIndex > maxIndex) {
            newIndex = 0;
            isWrapping = true;
          } else if (newIndex < 0) {
            newIndex = maxIndex;
            isWrapping = true;
          }
        } else {
          newIndex = Math.max(0, Math.min(maxIndex, newIndex));
        }
        if (newIndex === desktopIndex) return;
        const oldDesktopIndex = desktopIndex;
        desktopIndex = newIndex;
        isAnimating = true;
        if (animationLockTimer) window.clearTimeout(animationLockTimer);
        animationLockTimer = window.setTimeout(() => {
          isAnimating = false;
          animationLockTimer = null;
        }, 500);
        if (isWrapping) {
          applyDesktopWrap(oldDesktopIndex);
        } else {
          applyDesktopTransforms();
        }
        updateControls();
        return;
      }

      const slides = getSlides();
      if (!slides.length) return;
      const currentIndex = findCenterActiveIndex();
      const maxIndex = slides.length - 1;
      let targetIndex = currentIndex + direction;

      if (loopSlides) {
        if (targetIndex > maxIndex) targetIndex = 0;
        if (targetIndex < 0) targetIndex = maxIndex;
      } else {
        targetIndex = Math.max(0, Math.min(maxIndex, targetIndex));
      }

      scrollToCenterSlide(targetIndex);
    };

    const clearAutoplay = () => {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const setAutoplay = () => {
      clearAutoplay();
      if (!autoplayEnabled || autoplayStoppedByInteraction) return;
      if (getSlides().length < 2) return;
      if (!Number.isFinite(autoplaySpeed) || autoplaySpeed <= 0) return;

      autoplayTimer = window.setInterval(() => {
        stepByDirection(1);
      }, autoplaySpeed);
    };

    const handleInteraction = () => {
      if (!autoplayEnabled) return;
      if (stopAutoplayOnInteraction) {
        autoplayStoppedByInteraction = true;
        clearAutoplay();
        return;
      }
      setAutoplay();
    };

    if (prevButton) {
      prevButton.addEventListener("click", () => {
        stepByDirection(-1);
        handleInteraction();
      });
    }

    if (nextButton) {
      nextButton.addEventListener("click", () => {
        stepByDirection(1);
        handleInteraction();
      });
    }

    indicators.forEach((dot) => {
      dot.addEventListener("click", () => {
        const index = Number.parseInt(dot.dataset.index || "0", 10);
        if (isDesktop) {
          const slides = getSlides();
          const visibleCount = computeVisibleCount();
          const maxIndex = Math.max(0, slides.length - visibleCount);
          desktopIndex = Math.max(0, Math.min(maxIndex, index));
          applyDesktopTransforms();
          updateControls();
        } else {
          scrollToCenterSlide(index);
        }
        handleInteraction();
      });
    });

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let hasDragged = false;
    let dragStartIndex = 0;
    let lastScrollLeft = 0;
    let pendingScrollDirection = 0;

    const updateMobileActiveDuringSwipe = () => {
      if (isDesktop) return;
      setMobileActiveIndex(findActiveIndexEarly(pendingScrollDirection));
    };

    const startDrag = (x) => {
      isDragging = true;
      hasDragged = false;
      startX = x;
      startScrollLeft = track.scrollLeft;
      dragStartIndex = findCenterActiveIndex();
      lastScrollLeft = track.scrollLeft;
      track.classList.add("is-dragging");
      handleInteraction();
    };

    const moveDrag = (x) => {
      if (!isDragging) return;

      const deltaX = x - startX;
      if (Math.abs(deltaX) > DRAG_THRESHOLD) {
        hasDragged = true;
      }

      const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
      track.scrollLeft = Math.max(
        0,
        Math.min(startScrollLeft - deltaX, maxScrollLeft),
      );

      if (!isDesktop && hasDragged) {
        pendingScrollDirection = Math.sign(track.scrollLeft - startScrollLeft);
        updateMobileActiveDuringSwipe();
      }
    };

    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      track.classList.remove("is-dragging");
      if (hasDragged) {
        track.dataset.preventClick = "true";
        if (!isDesktop) {
          pendingScrollDirection = Math.sign(track.scrollLeft - startScrollLeft);
          setMobileActiveIndex(getSwipeTargetIndex(0.5));
        }
      }
      updateControls();
    };

    track.addEventListener("mousedown", (event) => {
      if (isDesktop) return;
      if (event.button !== 0) return;
      startDrag(event.clientX);
    });

    window.addEventListener("mousemove", (event) => {
      if (!isDragging) return;
      if (hasDragged) event.preventDefault();
      moveDrag(event.clientX);
    });

    window.addEventListener("mouseup", endDrag);

    track.addEventListener(
      "touchstart",
      (event) => {
        if (isDesktop) return;
        if (!event.touches || !event.touches.length) return;
        startDrag(event.touches[0].clientX);
      },
      { passive: true },
    );

    window.addEventListener(
      "touchmove",
      (event) => {
        if (!isDragging || !event.touches || !event.touches.length) return;
        const x = event.touches[0].clientX;
        const deltaX = x - startX;
        if (Math.abs(deltaX) > DRAG_THRESHOLD) {
          hasDragged = true;
          event.preventDefault();
        }
        moveDrag(x);
      },
      { passive: false },
    );

    window.addEventListener("touchend", endDrag);
    window.addEventListener("touchcancel", endDrag);

    track.addEventListener("dragstart", (event) => event.preventDefault());

    track.addEventListener(
      "click",
      (event) => {
        if (track.dataset.preventClick === "true") {
          event.preventDefault();
          event.stopPropagation();
          track.dataset.preventClick = "false";
        }
      },
      true,
    );

    let scrollRaf = null;
    const scheduleScrollUpdate = () => {
      if (isDesktop) return;
      if (scrollRaf) return;
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = null;
        const scrollDirection = Math.sign(track.scrollLeft - lastScrollLeft);
        if (scrollDirection) {
          pendingScrollDirection = scrollDirection;
        }
        lastScrollLeft = track.scrollLeft;
        updateMobileActiveDuringSwipe();
      });
    };

    track.addEventListener("scroll", scheduleScrollUpdate, { passive: true });

    if ("onscrollend" in window) {
      track.addEventListener("scrollend", () => {
        if (isDesktop) return;
        pendingScrollDirection = 0;
        updateControls();
      });
    }

    const handleViewportChange = () => {
      const wasDesktop = isDesktop;
      isDesktop = desktopMQ.matches;

      if (isDesktop) {
        getSlides().forEach((s) => s.classList.remove("is-active"));
        if (!wasDesktop) {
          track.scrollLeft = 0;
          desktopIndex = 0;
        } else {
          const slides = getSlides();
          const visibleCount = computeVisibleCount();
          const maxIndex = Math.max(0, slides.length - visibleCount);
          desktopIndex = Math.min(desktopIndex, maxIndex);
        }
        applyDesktopTransforms();
      } else {
        clearDesktopStyles();
        if (wasDesktop) {
          window.requestAnimationFrame(() => {
            scrollToCenterSlide(0, "auto");
            updateControls();
          });
          return;
        }
      }
      updateControls();
    };

    if (typeof desktopMQ.addEventListener === "function") {
      desktopMQ.addEventListener("change", handleViewportChange);
    } else if (typeof desktopMQ.addListener === "function") {
      desktopMQ.addListener(handleViewportChange);
    }

    window.addEventListener("resize", () => {
      handleViewportChange();
      if (!autoplayStoppedByInteraction) {
        setAutoplay();
      }
    });

    section.addEventListener("mouseenter", clearAutoplay);
    section.addEventListener("mouseleave", () => {
      if (!autoplayStoppedByInteraction) {
        setAutoplay();
      }
    });

    handleViewportChange();
    lastScrollLeft = track.scrollLeft;
    if (!isDesktop) {
      window.requestAnimationFrame(() => {
        scrollToCenterSlide(0, "auto");
        updateControls();
      });
    }
    setAutoplay();
  }

  function initializeAll() {
    document
      .querySelectorAll("[data-why-us-slider-section]")
      .forEach(initializeSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeAll);
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", initializeAll);
})();
